import { useMutation, useQueryClient } from '@tanstack/react-query';
import partition from 'lodash/partition';

import { parseApiError } from '@cast/api-client';
import { notify } from '@cast/design-system';
import { apiClient } from '@cast/infrastructure-api';
import { WorkloadScalingPolicy } from '@cast/types';

import { getWorkloadQueryKey } from 'hooks/queries/workload-optimization/useWorkloadQuery';
import { getWorkloadScalingPoliciesQueryKey } from 'hooks/queries/workload-optimization/useWorkloadScalingPoliciesQuery';
import { getWorkloadsQueryKey } from 'hooks/queries/workload-optimization/useWorkloadsQuery';

type UseBatchUpdateScalingPoliciesMutationProps = {
  onSuccess?: (allPoliciesCount: number, successCount: number) => void;
  onError?: () => void;
};

type TransformPolicyFn = (
  policy: WorkloadScalingPolicy
) => WorkloadScalingPolicy | undefined;

type BatchUpdateScalingPoliciesArgs = {
  policies: WorkloadScalingPolicy[];
  transformPolicy: TransformPolicyFn;
};

type PolicyUpdatePair = {
  policy: WorkloadScalingPolicy;
  updatedPolicy: WorkloadScalingPolicy;
};

const updatePolicy = async ({ policy, updatedPolicy }: PolicyUpdatePair) => {
  return apiClient.workloadOptimization.updateWorkloadScalingPolicy({
    clusterId: policy.clusterId,
    policyId: policy.id,
    policy: updatedPolicy,
  });
};

export const useBatchUpdateScalingPoliciesMutation = ({
  onSuccess,
  onError,
}: UseBatchUpdateScalingPoliciesMutationProps = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      policies,
      transformPolicy,
    }: BatchUpdateScalingPoliciesArgs) => {
      const policiesToUpdate: PolicyUpdatePair[] = policies
        .map((policy) => {
          const updatedPolicy = transformPolicy(policy);
          if (!updatedPolicy) {
            return null;
          }
          return { policy, updatedPolicy };
        })
        .filter((pair): pair is PolicyUpdatePair => pair !== null);

      const updateFns = policiesToUpdate.map(
        (pair) => () => updatePolicy(pair)
      );

      return Promise.allSettled(updateFns.map((fn) => fn()));
    },
    onSuccess: async (results, variables) => {
      await queryClient.invalidateQueries({
        queryKey: getWorkloadScalingPoliciesQueryKey(),
        predicate: ({ queryKey }) =>
          [
            getWorkloadScalingPoliciesQueryKey()[0],
            getWorkloadQueryKey()[0],
            getWorkloadsQueryKey()[0],
          ].includes(queryKey[0] as string),
      });

      const [fulfilled, rejected] = partition(
        results,
        (entry) => entry.status === 'fulfilled'
      );

      if (rejected.length > 0) {
        const firstError = rejected[0];
        notify.error(parseApiError(firstError.reason));
      }

      onSuccess?.(variables.policies.length, fulfilled.length);
    },
    onError: (error) => {
      notify.error(parseApiError(error));
      onError?.();
    },
  });
};
